import { createCoupleSchema, type CreateCoupleSchema } from '@/api/couples/couple.schema';
import { Button } from '@/components/ui/button';
import { ContentLoader } from '@/components/ui/loaders';
import { date } from '@/lib/date';
import { printFicha } from '@/lib/print/print-actions';
import { usePrintPreview } from '@/lib/print/use-print-preview';
import { useCurrentUser } from '@/services/auth';
import {
  useCouple,
  useCreateCouple,
  useDeleteCouple,
  useUpdateCouple,
} from '@/services/couples';
import { useEvent, useEvents } from '@/services/events';
import { zodResolver } from '@hookform/resolvers/zod';
import { Role } from 'generated/prisma/enums';
import { useEffect } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AdminFormPage, useServerErrors } from './admin-form';
import { CoupleFields, EMPTY_COUPLE, type CoupleFormValues } from './couple-fields';
import { ChoiceGroup, FormSection, SelectField, TextField } from './fields';
import { ACTIVE_OPTIONS, maskCep, maskPhone } from './sacraments';

const LIST = '/admin/couples';
const SECTION = 'rounded-[18px]';

export function CoupleForm({ id }: { id?: string }) {
  const navigate = useNavigate();
  const print = usePrintPreview();
  const { data: user } = useCurrentUser();

  const { data: couple, isLoading } = useCouple(id ?? '');
  const { data: nextEvent } = useEvent('next');
  const { data: events } = useEvents({
    perPage: 100,
    orderBy: 'startDate',
    order: 'desc',
  });

  const create = useCreateCouple();
  const update = useUpdateCouple();
  const remove = useDeleteCouple();
  const saving = create.isPending || update.isPending;

  const form = useForm<CoupleFormValues>({
    resolver: zodResolver(createCoupleSchema) as unknown as Resolver<CoupleFormValues>,
    defaultValues: EMPTY_COUPLE,
  });
  const { errors } = form.formState;

  useServerErrors(form, create.error ?? update.error);

  useEffect(() => {
    if (!couple) return;
    form.reset({
      husbandName: couple.husbandName,
      husbandPhone: maskPhone(couple.husbandPhone),
      wifeName: couple.wifeName,
      wifePhone: maskPhone(couple.wifePhone),
      email: couple.email,
      cep: maskCep(couple.cep),
      address: couple.address,
      addressNumber: couple.addressNumber ?? '',
      addressComplement: couple.addressComplement ?? '',
      baptismStatus: couple.baptismStatus,
      confirmationStatus: couple.confirmationStatus,
      marriageSacrament: couple.marriageSacrament,
      marriageDate: couple.marriageDate
        ? date(couple.marriageDate).format('YYYY-MM-DD')
        : '',
      parish: couple.parish ?? '',
      pastoralMovement: couple.pastoralMovement ?? '',
      childrenInfo: couple.childrenInfo ?? '',
      eventId: couple.eventId,
      isActive: couple.isActive,
    });
  }, [couple]);

  // Novo casal: o padrão é o próximo encontro.
  useEffect(() => {
    if (!id && nextEvent && !form.getValues('eventId'))
      form.setValue('eventId', nextEvent.id);
  }, [id, nextEvent]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const data = values as unknown as CreateCoupleSchema;
    try {
      if (id) await update.mutateAsync({ id, data });
      else await create.mutateAsync(data);
      navigate(LIST);
    } catch {
      // erros exibidos via toast / useServerErrors
    }
  });

  const handleDelete = async () => {
    if (!id) return;
    try {
      await remove.mutateAsync(id);
      navigate(LIST);
    } catch {
      // toast de erro exibido pelo hook
    }
  };

  const handlePrint = () => {
    const values = form.getValues();
    const event = events?.data.find((e) => e.id === values.eventId) ?? couple?.event;
    printFicha(print.open, event, {
      ...values,
      marriageSacrament: values.marriageSacrament ?? undefined,
      baptismStatus: values.baptismStatus || undefined,
      confirmationStatus: values.confirmationStatus || undefined,
    });
  };

  const eventOptions = (events?.data ?? []).map((e) => ({
    value: e.id,
    label: e.description,
  }));

  return (
    <>
      <ContentLoader show={(!!id && isLoading) || saving || remove.isPending} />
      <AdminFormPage
        backTo={LIST}
        backLabel="Voltar para Casais"
        eyebrow="CASAIS"
        title={id ? 'Editar casal' : 'Novo casal'}
        description="Dados do casal, endereço e caminhada de fé."
        headerAction={
          id && (
            <Button type="button" variant="outline" onClick={handlePrint}>
              Imprimir ficha
            </Button>
          )
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(LIST)}
        saveLabel={id ? 'Salvar alterações' : 'Criar cadastro'}
        saving={saving}
        onDelete={id && user?.role === Role.ADMIN ? handleDelete : undefined}
      >
        <CoupleFields form={form} variant="admin" sectionClassName={SECTION} />

        <FormSection
          eyebrow="04 • FAMÍLIA E INSCRIÇÃO"
          title="Família e inscrição"
          className={SECTION}
        >
          <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
            <TextField
              wrapperClassName="col-span-full"
              label="Filhos (quantidade / idades)"
              placeholder="Ex: 2 filhos (6, 9 anos)"
              {...form.register('childrenInfo')}
            />
            <Controller
              control={form.control}
              name="eventId"
              render={({ field }) => (
                <SelectField
                  label="Encontro *"
                  placeholder="Selecione"
                  options={eventOptions}
                  error={errors.eventId?.message}
                  {...field}
                />
              )}
            />
            <Controller
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <ChoiceGroup
                  label="Status da inscrição"
                  options={ACTIVE_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </FormSection>
      </AdminFormPage>
      {print.dialog}
    </>
  );
}
